import ApolloClient from 'apollo-client';
import fetchPonyfill from 'fetch-ponyfill';
import { createPersistedQueryLink } from 'apollo-link-persisted-queries';
import { ApolloLink } from 'apollo-link';
import { HttpLink } from 'apollo-link-http';
import { InMemoryCache, IntrospectionFragmentMatcher } from 'apollo-cache-inmemory';
import { onError } from 'apollo-link-error';
import _ from 'lodash';

import PublicEnv from 'shared/utils/public-env';
import possibleTypes from 'shared/schemas/fragment-types';
import { ADMIN_ALWAYS_LATEST } from 'shared/constants/api-versions';
import { LD_SESSION_REPLAY_HEADER, getLDSessionReplayUrl } from 'shared/observability/ld-observability';
import { VersionLink } from './version-link';

const { fetch } = fetchPonyfill({});

export const EXTENSION_ERRORS = {
  AUTHERROR: ['AUTHENTICATION_ERROR', 'UNAUTHENTICATED'],
  FORBIDDEN: ['FORBIDDEN'],
  SESSIONEXPIRED: ['SESSION_INVALID'],
};

const errorLinkFactory = ({ onSessionExpired, onAuthError } = {}) =>
  onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach((error) => {
        if (EXTENSION_ERRORS.FORBIDDEN.includes(error.extensions?.code)) {
          // console.error('Forbidden');
          onAuthError?.call(this, error);
        }

        if (EXTENSION_ERRORS.AUTHERROR.includes(error.extensions?.code)) {
          // console.error('Auth error');
          onSessionExpired?.call(this, error);
        }

        if (EXTENSION_ERRORS.SESSIONEXPIRED.includes(error.extensions?.code)) {
          // console.error('Session expired');
          onSessionExpired?.call(this, error);
        }
      });
    }
    if (networkError) {
      console.error(`Network Error: ${networkError.message}`);
    }
  });

export const setAuthorization = (operation, forward) => {
  const headers = {};
  if (typeof window !== 'undefined') {
    const token = window.localStorage.getItem('access-token');
    headers.url = window.location.href;

    if (token) {
      headers.authorization = token;
    }

    // Admin always gets latest API features (needs to manage all data).
    // @see shared/constants/api-versions.ts for available version constants.
    headers['x-api-version'] = ADMIN_ALWAYS_LATEST;

    const ldSessionReplayUrl = getLDSessionReplayUrl();
    if (ldSessionReplayUrl) {
      headers[LD_SESSION_REPLAY_HEADER] = ldSessionReplayUrl;
    }
  }

  operation.setContext(() => ({
    headers,
  }));

  return forward(operation);
};

const appMapping = {
  admin: 'Admin',
  brands: 'Brands',
};

const fragmentMatcher = new IntrospectionFragmentMatcher({
  introspectionQueryResultData: possibleTypes,
});

// Ensure fetch is bound to window (some environments break on unbound window.fetch),
// falling back to fetch-ponyfill when window.fetch isn't available (e.g. SSR).
const fetcher = {};
// eslint-disable-next-line lodash/prefer-lodash-typecheck
if (typeof window !== 'undefined' && typeof window.fetch === 'function') {
  fetcher.fetch = (...args) => window.fetch(...args);
  // eslint-disable-next-line lodash/prefer-lodash-typecheck
} else if (typeof fetch !== 'undefined') {
  fetcher.fetch = fetch;
}

function setupMainApolloClient(appKey, { onSessionExpired, onAuthError, onUpdateVersion = _.noop } = {}) {
  const appName = _.get(appMapping, appKey, '(NO APP SET)');
  const versionLinkInstance = VersionLink(onUpdateVersion);
  const errorLink = errorLinkFactory({ onSessionExpired, onAuthError });
  const httpLink = new HttpLink({
    credentials: 'include',
    uri: PublicEnv.graphqlUri,
    useGETForHashedQueries: true,
    ...fetcher,
  });
  return new ApolloClient({
    name: `Dutchie ${appName} Express GraphQL (${PublicEnv.appEnv})`,
    version: PublicEnv.currentVersion,
    cache: new InMemoryCache({ fragmentMatcher }),
    link: ApolloLink.from([
      versionLinkInstance,
      setAuthorization,
      errorLink,
      createPersistedQueryLink({ useGETForHashedQueries: true }),
      httpLink,
    ]),
  });
}

export default { setupMainApolloClient };
